import React, { useState, useEffect } from 'react';
import { 
  Table, 
  Button, 
  Switch, 
  message, 
  Popconfirm, 
  Modal, 
  Form, 
  Select, 
  DatePicker, 
  InputNumber,
  Card,
  Space,
  Typography,
  Tag,
  Input,
  Tooltip,
  Badge,
  Dropdown,
  Menu
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  ScheduleOutlined,
  SearchOutlined,
  ReloadOutlined,
  CalendarOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  StopOutlined,
  SyncOutlined,
  TagsOutlined,
  CloseOutlined, 
} from '@ant-design/icons';
import axios from 'axios';
import config from '../config';
import { adminInstance } from './api/axiosConfig';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

const { Option } = Select;
const { Title, Text } = Typography;
const { Search } = Input;

const WorkflowManagement = () => {
  const [workflows, setWorkflows] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTagModalVisible, setIsTagModalVisible] = useState(false);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [activeWorkflowId, setActiveWorkflowId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [tagFilters, setTagFilters] = useState([]);
  const [newTagInput, setNewTagInput] = useState('');
  const [form] = Form.useForm();
  const [executingWorkflows, setExecutingWorkflows] = useState({});
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${config.apiSettings.root}wp-ai-workflows/v1/workflows`, {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce
        }
      });
      if (Array.isArray(response.data)) {
        setWorkflows(response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
      } else {
        message.error('Received invalid data format from server');
      }
    } catch (error) {
      message.error('Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  };

  const getTagColor = (tag) => {
    const colors = [
      'magenta', 'red', 'volcano', 'orange', 'gold',
      'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple'
    ];
    const index = Array.from(tag).reduce(
      (acc, char) => acc + char.charCodeAt(0), 0
    ) % colors.length;
    return colors[index];
  };

  const handleAddTag = async (workflowId, tag) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;

    const tags = workflow.tags || [];
    if (tags.some(t => t.name.toLowerCase() === tag.toLowerCase())) {
      message.warning('This tag already exists');
      return;
    }

    const updatedWorkflow = {
      ...workflow,
      tags: [...tags, { 
        id: Date.now(),
        name: tag,
        color: getTagColor(tag)
      }]
    };

    try {
      await axios.put(
        `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`,
        updatedWorkflow,
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      fetchWorkflows();
      message.success('Tag added successfully');
    } catch (error) {
      message.error('Failed to add tag');
    }
  };

  const handleRemoveTag = async (workflowId, tagId) => {
    const workflow = workflows.find(w => w.id === workflowId);
    if (!workflow) return;

    const updatedWorkflow = {
      ...workflow,
      tags: (workflow.tags || []).filter(tag => tag.id !== tagId)
    };

    try {
      await axios.put(
        `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${workflowId}`,
        updatedWorkflow,
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      fetchWorkflows();
      message.success('Tag removed successfully');
    } catch (error) {
      message.error('Failed to remove tag');
    }
  };

  const filterByTags = (workflows) => {
    if (tagFilters.length === 0) return workflows;
    return workflows.filter(workflow => 
      workflow.tags?.some(tag => tagFilters.includes(tag.name))
    );
  };

  const showAddTagModal = (workflowId) => {
    setActiveWorkflowId(workflowId);
    setIsTagModalVisible(true);
  };

  const getUniqueTags = (workflows) => {
    const tagMap = new Map();
    workflows.forEach(workflow => {
      (workflow.tags || []).forEach(tag => {
        tagMap.set(tag.name, tag);
      });
    });
    return Array.from(tagMap.values());
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'success',
      inactive: 'default',
      scheduled: 'processing'
    };
    return colors[status] || 'default';
  };

  const getScheduleText = (schedule) => {
    if (!schedule || !schedule.enabled) return null;
    return `Every ${schedule.interval} ${schedule.unit}${schedule.interval > 1 ? 's' : ''}`;
  };

  const renderScheduleBadge = (schedule) => {
    if (!schedule || !schedule.enabled) return null;
    return (
      <Tooltip title={
        <>
          <div>Runs: {getScheduleText(schedule)}</div>
          {schedule.endDate && <div>Until: {dayjs(schedule.endDate).format('YYYY-MM-DD')}</div>}
        </>
      }>
        <Tag icon={<ClockCircleOutlined />} color="blue">
          Scheduled
        </Tag>
      </Tooltip>
    );
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => {
      setSelectedRowKeys(newSelectedRowKeys);
    },
  };
  
  // Add bulk delete handler
  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedRowKeys.map(id =>
          axios.delete(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${id}`, {
            headers: { 'X-WP-Nonce': config.apiSettings.nonce }
          })
        )
      );
      message.success(`${selectedRowKeys.length} workflows deleted successfully`);
      setSelectedRowKeys([]);
      fetchWorkflows();
    } catch (error) {
      console.error('Error deleting workflows:', error);
      message.error('Failed to delete workflows');
    }
  };

  const handleBulkActivate = async (activate = true) => {
    try {
      await Promise.all(
        selectedRowKeys.map(id =>
          axios.put(
            `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${id}`, 
            { status: activate ? 'active' : 'inactive' },
            { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
          )
        )
      );
      message.success(`${selectedRowKeys.length} workflows ${activate ? 'activated' : 'deactivated'} successfully`);
      fetchWorkflows();
    } catch (error) {
      message.error(`Failed to ${activate ? 'activate' : 'deactivate'} workflows`);
    }
  };
  
  const handleBulkAddTag = async (tag) => {
    try {
      const updates = selectedRowKeys.map(async (id) => {
        const workflow = workflows.find(w => w.id === id);
        if (!workflow) return;
  
        const tags = workflow.tags || [];
        if (tags.some(t => t.name === tag)) return;
  
        const updatedWorkflow = {
          ...workflow,
          tags: [...tags, { 
            id: Date.now(),
            name: tag,
            color: getTagColor(tag)
          }]
        };
  
        await axios.put(
          `${config.apiSettings.root}wp-ai-workflows/v1/workflows/${id}`,
          updatedWorkflow,
          { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
        );
      });
  
      await Promise.all(updates);
      message.success('Tags added successfully');
      fetchWorkflows();
    } catch (error) {
      message.error('Failed to add tags');
    }
  };

  const bulkActions = (
    <div style={{ 
      display: selectedRowKeys.length > 0 ? 'flex' : 'none',
      padding: '12px 24px',
      backgroundColor: '#f5f5f5',
      borderRadius: '6px',
      marginBottom: '16px',
      justifyContent: 'space-between',
      alignItems: 'center'
    }}>
      <Space>
        <Text>{selectedRowKeys.length} items selected</Text>
        <Button 
          type="text" 
          onClick={() => setSelectedRowKeys([])}
          size="small"
        >
          Clear selection
        </Button>
      </Space>
      <Space>
        <Select
          placeholder="Add tag to selected"
          style={{ width: 200 }}
          onChange={handleBulkAddTag}
          value={undefined}
        >
          {getUniqueTags(workflows).map(tag => (
            <Option key={tag.name} value={tag.name}>
              <Tag color={tag.color}>{tag.name}</Tag>
            </Option>
          ))}
        </Select>
        <Button 
          onClick={() => handleBulkActivate(true)}
          icon={<CheckCircleOutlined />}
        >
          Activate
        </Button>
        <Button 
          onClick={() => handleBulkActivate(false)}
          icon={<StopOutlined />}
        >
          Deactivate
        </Button>
        <Popconfirm
          title="Delete Selected Workflows"
          description={`Are you sure you want to delete ${selectedRowKeys.length} workflows?`}
          onConfirm={handleBulkDelete}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button 
            danger
            icon={<DeleteOutlined />}
          >
            Delete Selected
          </Button>
        </Popconfirm>
      </Space>
    </div>
  );

  const columns = [
    {
      title: 'Workflow',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {executingWorkflows[record.id]?.executing ? (
            <Space>
              <SyncOutlined spin style={{ color: '#1890ff' }} />
              <Tag color="processing">
                {executingWorkflows[record.id].currentNode || 'Executing...'}
              </Tag>
            </Space>
          ) : (
            <Badge status={record.status === 'active' ? 'success' : 'default'} />
          )}
          <span>{name}</span>
        </Space>
      ),
    },
    {
      title: 'Tags',
      key: 'tags',
      width: 250,
      render: (_, record) => (
        <Space wrap>
          {record.tags?.map(tag => (
            <Tag
              key={tag.id}
              color={tag.color}
              closable
              onClose={(e) => {
                e.preventDefault();
                handleRemoveTag(record.id, tag.id);
              }}
            >
              {tag.name}
            </Tag>
          ))}
          <Tag
            style={{ borderStyle: 'dashed', cursor: 'pointer' }}
            onClick={() => showAddTagModal(record.id)}
          >
            <PlusOutlined />
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date) => (
        <Space direction="vertical" size={0}>
          <span>{dayjs(date).format('YYYY-MM-DD')}</span>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {dayjs(date).format('HH:mm')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 150,
      render: (user) => (
        <Space>
          <UserOutlined />
          <span>{user}</span>
        </Space>
      ),
    },
    {
      title: 'Last Execution',
      key: 'lastExecuted',
      width: 200,
      render: (_, record) => {
        if (!record.lastExecuted) {
          return <span>Never</span>;
        }
    
        // Create UTC moment explicitly
        const executionTime = dayjs.utc(record.lastExecuted);
        // Get relative time using UTC
        const relativeTime = executionTime.fromNow();
    
        return (
          <Space direction="vertical" size={0}>
            <span>{relativeTime}</span>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {executionTime.format('YYYY-MM-DD HH:mm')} UTC
            </Text>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      width: 100,
      render: (_, record) => (
        <Switch 
          checked={record.status === 'active'} 
          onChange={(checked) => handleStatusChange(record.id, checked)}
          size="small"
        />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title={record.schedule?.enabled ? 'Edit Schedule' : 'Schedule'}>
            <Button
              type={record.schedule?.enabled ? 'primary' : 'default'}
              icon={<ScheduleOutlined />}
              onClick={() => handleScheduleClick(record)}
              size="middle"
              shape="circle"
            />
          </Tooltip>
    
          <Tooltip title="Run Workflow">
            <Button
              type="default"
              icon={<PlayCircleOutlined spin={executingWorkflows[record.id]} />}
              onClick={() => handleRunWorkflow(record.id)}
              loading={executingWorkflows[record.id]}
              disabled={executingWorkflows[record.id]}
              size="middle"
              shape="circle"
            />
          </Tooltip>
    
          <Tooltip title="Edit">
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record.id)}
              size="middle"
              shape="circle"
            />
          </Tooltip>
          
    
          <Tooltip title="Delete">
          <Popconfirm
            title="Delete Workflow"
            description="Are you sure you want to delete this workflow?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="default"
              danger
              icon={<DeleteOutlined />}
              size="middle"
              shape="circle"
            />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];
  
  const handleRunWorkflow = async (workflowId) => {
    try {
      setExecutingWorkflows(prev => ({ 
        ...prev, 
        [workflowId]: {
          executing: true,
          currentNode: 'Starting...',
          executionId: null
        }
      }));
      
      const response = await adminInstance.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/execute-workflow/${workflowId}`
      );
  
      const executionId = response.data.execution_id;
      if (!executionId) {
        throw new Error('Invalid response from server: No execution ID received');
      }
  
      setExecutingWorkflows(prev => ({ 
        ...prev, 
        [workflowId]: {
          ...prev[workflowId],
          executionId
        }
      }));
  
      let pollInterval;
      let timeoutCounter = 0;
      const MAX_TIMEOUT = 600;
  
      pollInterval = setInterval(async () => {
        try {
          timeoutCounter++;
          if (timeoutCounter >= MAX_TIMEOUT) {
            clearInterval(pollInterval);
            message.error('Execution timed out after 4 minutes');
            setExecutingWorkflows(prev => {
              const newState = { ...prev };
              delete newState[workflowId];
              return newState;
            });
            return;
          }
  
          const statusResponse = await adminInstance.get(
            `${config.apiSettings.root}wp-ai-workflows/v1/execution-status/${executionId}`
          );
  
          const { status, current_node, is_complete } = statusResponse.data;
  
          if (current_node) {
            setExecutingWorkflows(prev => ({ 
              ...prev, 
              [workflowId]: {
                ...prev[workflowId],
                currentNode: `Processing ${current_node}...`
              }
            }));
          }
  
          if (is_complete || status === 'completed' || status === 'error') {
            clearInterval(pollInterval);
            setExecutingWorkflows(prev => {
              const newState = { ...prev };
              delete newState[workflowId];
              return newState;
            });
            
            if (status === 'error') {
              message.error('Workflow execution failed');
            } else {
              message.success('Workflow executed successfully');
            }
            
            fetchWorkflows();
          }
        } catch (error) {
          console.error('Error polling execution status:', error);
          clearInterval(pollInterval);
          setExecutingWorkflows(prev => {
            const newState = { ...prev };
            delete newState[workflowId];
            return newState;
          });
          message.error('Error during workflow execution');
        }
      }, 500);
  
    } catch (error) {
      console.error('Error executing workflow:', error);
      setExecutingWorkflows(prev => {
        const newState = { ...prev };
        delete newState[workflowId];
        return newState;
      });
      message.error('Failed to execute workflow: ' + (error.response?.data?.message || error.message));
    }
  };
  
  useEffect(() => {
    return () => {
      Object.keys(executingWorkflows).forEach(workflowId => {
        if (executingWorkflows[workflowId]) {
          setExecutingWorkflows(prev => ({ ...prev, [workflowId]: false }));
        }
      });
    };
  }, []);

  const handleStatusChange = async (id, checked) => {
    try {
      await axios.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${id}`, 
        { status: checked ? 'active' : 'inactive' },
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      message.success(`Workflow ${checked ? 'activated' : 'deactivated'} successfully`);
      fetchWorkflows();
    } catch (error) {
      console.error('Error updating workflow status:', error);
      message.error('Failed to update workflow status');
    }
  };

  const handleEdit = (id) => {
    window.location.href = `/wp-admin/admin.php?page=wp-ai-workflows&action=edit&id=${id}`;
  };

  const handleCreate = () => {
    window.location.href = '/wp-admin/admin.php?page=wp-ai-workflows&action=builder';
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${id}`, {
        headers: { 'X-WP-Nonce': config.apiSettings.nonce }
      });
      message.success('Workflow deleted successfully');
      fetchWorkflows();
    } catch (error) {
      console.error('Error deleting workflow:', error);
      message.error('Failed to delete workflow');
    }
  };

  const handleScheduleClick = (workflow) => {
    setSelectedWorkflow(workflow);
    setIsModalVisible(true);
    const currentSchedule = workflow.schedule || {};
    form.setFieldsValue({
      ...currentSchedule,
      endDate: currentSchedule.endDate ? dayjs(currentSchedule.endDate) : null
    });
  };

  const handleScheduleSave = async (values) => {
    try {
      const updatedWorkflow = {
        ...selectedWorkflow,
        schedule: {
          ...values,
          endDate: values.endDate ? values.endDate.format('YYYY-MM-DD') : null,
          enabled: true
        }
      };
      await axios.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${selectedWorkflow.id}`, 
        updatedWorkflow,
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      message.success('Workflow schedule updated successfully');
      setIsModalVisible(false);
      fetchWorkflows();
    } catch (error) {
      console.error('Error updating workflow schedule:', error);
      message.error('Failed to update workflow schedule');
    }
  };

  const handleDeleteSchedule = async () => {
    try {
      const updatedWorkflow = {
        ...selectedWorkflow,
        schedule: {
          enabled: false
        }
      };
      await axios.put(`${config.apiSettings.root}wp-ai-workflows/v1/workflows/${selectedWorkflow.id}`, 
        updatedWorkflow,
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      message.success('Workflow schedule deleted successfully');
      setIsModalVisible(false);
      fetchWorkflows();
    } catch (error) {
      console.error('Error deleting workflow schedule:', error);
      message.error('Failed to delete workflow schedule');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>Workflows</Title>
          
          {selectedRowKeys.length > 0 ? (
            // Bulk Actions Bar
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <Text strong>{selectedRowKeys.length} items selected</Text>
                <Button 
                  size="middle"
                  icon={<CloseOutlined />}
                  onClick={() => setSelectedRowKeys([])}
                >
                  Clear Selection
                </Button>
              </Space>
  
              <Space>
                <Select
                  placeholder="Assign tag to selected items"
                  style={{ width: 250 }}
                  onChange={(value) => {
                    handleBulkAddTag(value);
                    // Reset the select value
                    setTimeout(() => {
                      const select = document.querySelector('.bulk-tag-select');
                      if (select) select.click();
                    }, 0);
                  }}
                  value={undefined}
                  className="bulk-tag-select"
                >
                  {getUniqueTags(workflows).map(tag => (
                    <Option key={tag.name} value={tag.name}>
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Option>
                  ))}
                </Select>
                <Button 
                  onClick={() => handleBulkActivate(true)}
                  icon={<CheckCircleOutlined />}
                >
                  Activate
                </Button>
                <Button 
                  onClick={() => handleBulkActivate(false)}
                  icon={<StopOutlined />}
                >
                  Deactivate
                </Button>
                <Popconfirm
                  title="Delete Selected Workflows"
                  description={`Are you sure you want to delete ${selectedRowKeys.length} workflows?`}
                  onConfirm={handleBulkDelete}
                  okText="Delete"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                >
                  <Button 
                    danger
                    icon={<DeleteOutlined />}
                  >
                    Delete Selected
                  </Button>
                </Popconfirm>
              </Space>
            </Space>
          ) : (
            // Normal Search/Filter Bar
            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
              <Space>
                <Select
                  mode="multiple"
                  placeholder="Filter by tags..."
                  value={tagFilters}
                  onChange={setTagFilters}
                  style={{ width: 300 }}
                  allowClear
                  optionLabelProp="label"
                >
                  {getUniqueTags(workflows).map(tag => (
                    <Option 
                      key={tag.id} 
                      value={tag.name}
                      label={tag.name}
                    >
                      <Tag color={tag.color}>{tag.name}</Tag>
                    </Option>
                  ))}
                </Select>
                <Input
                  placeholder="Search workflows by name..."
                  allowClear
                  onChange={(e) => setSearchText(e.target.value)}
                  style={{ width: 300 }}
                />
              </Space>
  
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreate}
              >
                Create a Workflow
              </Button>
            </Space>
          )}
  
          <Table 
            rowSelection={rowSelection}
            columns={columns} 
            dataSource={filterByTags(workflows).filter(w => 
              w.name.toLowerCase().includes(searchText.toLowerCase())
            )} 
            rowKey="id"
            loading={loading}
            pagination={{ 
              pageSize: 10,
              showTotal: (total) => `Total ${total} workflows`
            }}
            size="middle"
            onRow={() => ({
              style: {
                height: '52px',
              },
            })}
          />
        </Space>
  
        <Modal
          title={
            <Space>
              <CalendarOutlined />
              <span>Schedule Workflow</span>
            </Space>
          }
          open={isModalVisible}
          onCancel={() => setIsModalVisible(false)}
          footer={[
            <Button key="cancel" onClick={() => setIsModalVisible(false)}>
              Cancel
            </Button>,
            <Button 
              key="delete" 
              danger 
              icon={<DeleteOutlined />}
              onClick={handleDeleteSchedule}
            >
              Delete Schedule
            </Button>,
            <Button 
              key="submit" 
              type="primary" 
              icon={<ScheduleOutlined />}
              onClick={() => form.submit()}
            >
              Save Schedule
            </Button>,
          ]}
        >
          <Form 
            form={form} 
            onFinish={handleScheduleSave}
            layout="vertical"
          >
            <Space align="start" style={{ width: '100%' }}>
              <Form.Item 
                name="interval" 
                label="Repeat Every" 
                rules={[{ required: true }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={1} style={{ width: 100 }} />
              </Form.Item>
              <Form.Item 
                name="unit" 
                label="Unit" 
                rules={[{ required: true }]}
                style={{ marginBottom: 0 }}
              >
                <Select style={{ width: 150 }}>
                  <Option value="minute">Minute(s)</Option>
                  <Option value="hour">Hour(s)</Option>
                  <Option value="day">Day(s)</Option>
                  <Option value="week">Week(s)</Option>
                  <Option value="month">Month(s)</Option>
                </Select>
              </Form.Item>
            </Space>
            <Form.Item 
              name="endDate" 
              label="End Date (Optional)"
              style={{ marginTop: 16 }}
            >
              <DatePicker 
                format="YYYY-MM-DD"
                style={{ width: '100%' }}
                showToday
                disabledDate={(current) => {
                  return current && current < dayjs().startOf('day');
                }}
              />
            </Form.Item>
          </Form>
        </Modal>
  
        <Modal
          title={
            <Space>
              <TagsOutlined />
              <span>Add Tag</span>
            </Space>
          }
          open={isTagModalVisible}
          onCancel={() => {
            setIsTagModalVisible(false);
            setNewTagInput('');
          }}
          onOk={() => {
            if (newTagInput.trim()) {
              handleAddTag(activeWorkflowId, newTagInput.trim());
              setIsTagModalVisible(false);
              setNewTagInput('');
            }
          }}
          okButtonProps={{ 
            disabled: !newTagInput.trim(),
            icon: <PlusOutlined />
          }}
        >
          <Space direction="vertical" style={{ width: '100%' }} size="large">
            <div>
              <Text strong>New Tag</Text>
              <Input
                placeholder="Enter tag name"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onPressEnter={() => {
                  if (newTagInput.trim()) {
                    handleAddTag(activeWorkflowId, newTagInput.trim());
                    setIsTagModalVisible(false);
                    setNewTagInput('');
                  }
                }}
                prefix={<TagsOutlined style={{ color: '#bfbfbf' }} />}
                maxLength={50}
                showCount
              />
            </div>
  
            <div>
              <Text strong>Existing Tags</Text>
              <div style={{ 
                marginTop: 8,
                padding: 12,
                background: '#f5f5f5',
                borderRadius: 6,
                minHeight: 40
              }}>
                <Space wrap>
                  {getUniqueTags(workflows).map(tag => (
                    <Tag
                      key={tag.id}
                      color={tag.color}
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        handleAddTag(activeWorkflowId, tag.name);
                        setIsTagModalVisible(false);
                      }}
                    >
                      {tag.name}
                    </Tag>
                  ))}
                  {getUniqueTags(workflows).length === 0 && (
                    <Text type="secondary">No existing tags</Text>
                  )}
                </Space>
              </div>
            </div>
          </Space>
        </Modal>
      </Space>
    </div>
  );
};

export default WorkflowManagement;